import { makeAutoObservable, runInAction } from 'mobx';
import { v4 } from 'uuid';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ReportSettingDto, ReportSettingLevelDto,ReportSettingsDeleteDto } from '@src/api';

export type ReportSettingDtoDefId = Omit<ReportSettingDto, 'id' | 'code'> & { id: string; code: string };
export type ReportSettingLevelDtoDefId = Omit<ReportSettingLevelDto, 'id'> & { id: string };

export const LEVELS = {
  LEVEL1: '1', // Налоговая декларация
  LEVEL2: '2', // Сводный налоговый регистр
  LEVEL3: '3', // Реестр электронных карточек проводок
  LEVEL4_D01: '4', // Электронная карточка проводки для НнП"
  LEVEL4: '5', // Электронная карточка проводки"
  LEVEL5: '6', // Аналитический налоговый регистр
  LEVEL6: '7', // Книга покупок
  LEVEL7: '8', // Книга продаж
  LEVEL9: '9', // Журнал выставленных с/ф
  LEVEL10: '10', // Журнал полученных с/ф
};

type Filters = {
  reportGroup?: string;
  reportType?: string;
};

class ReportSettingsStore {
  reportSettings: ReportSettingDtoDefId[] = [];

  reportSettingsLevel: ReportSettingLevelDtoDefId[] = [];

  organizationCode = '';

  filters: Filters = {};

  breadcrumbs: string[] = [];

  fullSize = false;

  selectNode: ReportSettingDtoDefId | null = null;

  selectedKeys: string[] = [];

  expandedKeys: string[] = [];

  checked: ResponseDataRow[] = [];

  isChanged = false; // флаг, что справочник изменился для проверки загрузки в fetchReferences

  templateModalOpen = false;

  api: CmApi;

  isCopySettingsModalVisible = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  toggleCopySettingsModalVisible = () => (this.isCopySettingsModalVisible = !this.isCopySettingsModalVisible);

  setIsChanged = (value: boolean) => {
    this.isChanged = value;
  };

  setOrganizationCode = (code: string) => {
    this.organizationCode = code;
  };

  setFilters = (filter: Filters) => {
    this.filters = filter;
  };

  setBreadcrumbs = (value: string[]) => {
    this.breadcrumbs = value;
  };

  toggleFullSize = () => {
    this.fullSize = !this.fullSize;
  };

  setSelectNode = (value: ReportSettingDtoDefId | null) => {
    this.selectNode = value;

    if (value === null) {
      this.fullSize = false;
    }
  };

  setSelectedKeys = (value: string[]) => {
    this.selectedKeys = value;
  };

  setExpandedKeys = (value: string[]) => {
    this.expandedKeys = value;
  };

  setChecked = (value) => {
    this.checked = value;
  };

  toggleTemplateModal = () => {
    this.templateModalOpen = !this.templateModalOpen;
  };

  fetch = async (): Promise<void> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.reportSettings.getReportSettingsByOrganization(this.organizationCode);

    runInAction(() => {
      this.reportSettings =
        (data as ReportSettingDtoDefId[])?.map((item) => ({ ...item, id: item.reportVersionCode ?? '' })) ?? [];
      this.isChanged = false;
    });
    preloaderStore.preloader(false);
  };

  addReportSettings = async (report: ReportSettingDtoDefId): Promise<ReportSettingDto> => {
    preloaderStore.preloader(true);
    const { data } = await this.api.reportSettings.addReportSettings(report);

    preloaderStore.preloader(false);

    return data;
  };

  groupDelete = async (items: ReportSettingsDeleteDto[]) => {
    preloaderStore.preloader(true, 'удаление...');
    await this.api.reportSettings.deleteReportSettings(items);
    await this.fetch();
    preloaderStore.preloader(false);
  };

  clearOrganization = () => {
    this.reportSettings = [];
    this.organizationCode = '';
  };

  clear = () => {
    this.filters = {};
    this.breadcrumbs = [];
    this.fullSize = false;
    this.selectNode = null;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.checked = [];
    this.clearOrganization();
  };

  getReportSettingsLevel = async (): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.reportSettings.getReportSettingLevel();

      if (data) {
        runInAction(() => {
          this.reportSettingsLevel = data.map((item) => ({ ...item, id: String(item.id) ?? v4() }));
        });
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };
}

export const reportSettingsStore = new ReportSettingsStore({ api: cmApi });
